import { FORMAT_LABELS, FormatSchema, type Format } from '@ugin/shared';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHealth } from '@/hooks/useHealth';
import { useSettings } from '@/store/settings';
import { WidgetContainer } from '@/widgets/_base/WidgetContainer';

export function Settings() {
  const { data: health } = useHealth();
  const defaultFormat = useSettings((s) => s.defaultFormat);
  const setDefaultFormat = useSettings((s) => s.setDefaultFormat);
  const reducedMotion = useSettings((s) => s.reducedMotion);
  const setReducedMotion = useSettings((s) => s.setReducedMotion);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="font-display text-4xl tracking-wide text-ugin-text">Settings</h1>
        <p className="text-sm text-ugin-text-muted">
          Preferences kept in your browser. Secrets are configured during install.
        </p>
      </header>

      <WidgetContainer title="Preferences">
        <div className="space-y-5">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-ugin-text-muted">Default format</span>
            <Select value={defaultFormat} onValueChange={(v) => { setDefaultFormat(v as Format); }}>
              <SelectTrigger className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FormatSchema.options.map((f) => (
                  <SelectItem key={f} value={f}>
                    {FORMAT_LABELS[f]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={reducedMotion}
              onChange={(e) => { setReducedMotion(e.target.checked); }}
              className="size-4 accent-ugin-accent"
            />
            <span className="text-sm text-ugin-text">Reduce motion</span>
          </label>
        </div>
      </WidgetContainer>

      <WidgetContainer title="System">
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Row label="data-api version" value={health?.version ?? '—'} />
          <Row label="data-api uptime (s)" value={health?.uptime.toString() ?? '—'} />
          <Row label="DB healthy" value={health ? (health.dbOk ? 'yes' : 'no') : '—'} />
          <Row label="Token usage MTD" value={health ? `$${health.tokenUsageMtd.toFixed(2)}` : '—'} />
        </dl>
      </WidgetContainer>

      <WidgetContainer title="Secrets" emptyTitle="" emptyDescription="">
        <p className="text-sm text-ugin-text-muted">
          Ugin&rsquo;s Anthropic key lives in <code className="rounded bg-ugin-bg-deep px-1.5">/etc/ugin/env</code> on
          the server, readable only by the service account. It is never sent to the browser and is
          <strong> not</strong> editable here.
        </p>
      </WidgetContainer>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 rounded-md border border-ugin-border/30 bg-ugin-bg-deep/30 px-3 py-2">
      <dt className="text-ugin-text-muted">{label}</dt>
      <dd className="font-mono text-ugin-text">{value}</dd>
    </div>
  );
}
